import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { MaskLines } from "../components/Reveal";
import { useI18n } from "../i18n";
import { api, formatApiError } from "../lib/api";

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [err, setErr] = useState("");

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErr("");
    try {
      await api.post("/contact", form);
      setStatus("success");
      setForm({ name: "", email: "", company: "", interest: "", message: "" });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || t.contact.error);
      setStatus("error");
    }
  };

  const field = "w-full bg-transparent border-b border-[#F7F5F0]/25 py-4 font-body text-[#F7F5F0] text-lg placeholder-[#F7F5F0]/40 focus:outline-none focus:border-[#C5A880] transition-colors duration-300";

  return (
    <section className="min-h-screen bg-[#062029] grain overflow-hidden pt-40 pb-28" data-testid="contact-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-16">
        <div className="md:col-span-5">
          <p className="label text-[#C5A880] mb-8">{t.contact.label}</p>
          <h1 className="font-serif-display text-[#F7F5F0] text-4xl md:text-6xl leading-tight">
            <MaskLines lines={[t.contact.heading]} delay={0.15} />
          </h1>
          <Reveal delay={0.2}>
            <p className="font-body text-[#F7F5F0]/70 mt-10 text-lg leading-relaxed max-w-md">{t.contact.body}</p>
          </Reveal>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          {status === "success" ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-[#C5A880]/40 p-12 text-center"
              data-testid="contact-success"
            >
              <div className="w-14 h-14 rounded-full border border-[#C5A880] grid place-items-center mx-auto mb-6">
                <Check className="text-[#C5A880]" />
              </div>
              <p className="font-serif-display text-[#F7F5F0] text-3xl">{t.contact.success}</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-8">
                <input className={field} placeholder={t.contact.name} required value={form.name} onChange={change("name")} data-testid="contact-name" />
                <input className={field} type="email" placeholder={t.contact.email} required value={form.email} onChange={change("email")} data-testid="contact-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <input className={field} placeholder={t.contact.company} value={form.company} onChange={change("company")} data-testid="contact-company" />
                <input className={field} placeholder={t.contact.interest} value={form.interest} onChange={change("interest")} data-testid="contact-interest" />
              </div>
              <textarea className={field + " resize-none"} rows={4} placeholder={t.contact.message} required value={form.message} onChange={change("message")} data-testid="contact-message" />
              {status === "error" && <p className="text-[#e0a0a0] font-body text-sm" data-testid="contact-error">{err}</p>}
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="contact-submit"
                className="w-full sm:w-auto border border-[#C5A880]/60 text-[#F7F5F0] px-10 py-4 text-[12px] tracking-widest uppercase hover:bg-[#C5A880] hover:text-[#062029] transition-colors duration-500 disabled:opacity-50"
              >
                {status === "sending" ? t.contact.sending : t.contact.submit}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
